// Road routing (OSRM) and address search (Photon), both OpenStreetMap based.
// The public servers have fair-use limits; point these at your own instances for heavy use.
const OSRM_URL = import.meta.env.VITE_OSRM_URL || 'https://router.project-osrm.org';
const PHOTON_URL = import.meta.env.VITE_PHOTON_URL || 'https://photon.komoot.io';

export type LngLat = [number, number];

export interface RoadRoute {
  path: LngLat[];
  distance: number; // meters
  duration: number; // seconds
  legs: { distance: number; duration: number }[];
}

export interface AddressResult {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

const coordsParam = (points: LngLat[]) => points.map(([lng, lat]) => `${lng.toFixed(6)},${lat.toFixed(6)}`).join(';');

/** Road path through the points in the given order. */
export const fetchRoadRoute = async (points: LngLat[], signal?: AbortSignal): Promise<RoadRoute | null> => {
  if (points.length < 2) return null;
  const res = await fetch(
    `${OSRM_URL}/route/v1/driving/${coordsParam(points)}?overview=full&geometries=geojson`,
    { signal },
  );
  if (!res.ok) throw new Error(`Routing failed (${res.status})`);
  const data = await res.json();
  const route = data.routes?.[0];
  if (data.code !== 'Ok' || !route) return null;
  return {
    path: route.geometry.coordinates,
    distance: route.distance,
    duration: route.duration,
    legs: route.legs.map((leg: { distance: number; duration: number }) => ({ distance: leg.distance, duration: leg.duration })),
  };
};

/**
 * Best visiting order, keeping the first stop as the start (and the last as the end when keepLast).
 * Returns the stop indexes in visiting order.
 */
export const fetchOptimizedOrder = async (points: LngLat[], keepLast: boolean): Promise<number[]> => {
  if (points.length < 3) return points.map((_, index) => index);
  const params = `source=first&roundtrip=false&destination=${keepLast ? 'last' : 'any'}`;
  const res = await fetch(`${OSRM_URL}/trip/v1/driving/${coordsParam(points)}?${params}&overview=false`);
  if (!res.ok) throw new Error(`Optimization failed (${res.status})`);
  const data = await res.json();
  if (data.code !== 'Ok') throw new Error(data.message || 'Optimization failed');
  // waypoints[i].waypoint_index is where input point i lands in the trip.
  const order: number[] = new Array(points.length);
  data.waypoints.forEach((waypoint: { waypoint_index: number }, inputIndex: number) => {
    order[waypoint.waypoint_index] = inputIndex;
  });
  return order;
};

const formatAddress = (p: Record<string, string | undefined>) => {
  const street = [p.street, p.housenumber].filter(Boolean).join(' ');
  return [p.type === 'street' ? p.name : street || undefined, p.district || p.locality, p.city, p.state, p.country]
    .filter(Boolean)
    .join(', ');
};

export const searchAddress = async (query: string, near?: LngLat, signal?: AbortSignal): Promise<AddressResult[]> => {
  const params = new URLSearchParams({ q: query, limit: '6' });
  if (near) {
    params.set('lon', String(near[0]));
    params.set('lat', String(near[1]));
  }
  const res = await fetch(`${PHOTON_URL}/api/?${params}`, { signal });
  if (!res.ok) throw new Error(`Address search failed (${res.status})`);
  const data = await res.json();
  return data.features.map((feature: { properties: Record<string, string>; geometry: { coordinates: LngLat } }) => {
    const address = formatAddress(feature.properties);
    return {
      name: feature.properties.name || address.split(',')[0] || query,
      address,
      longitude: feature.geometry.coordinates[0],
      latitude: feature.geometry.coordinates[1],
    };
  });
};

export const reverseGeocode = async (point: LngLat): Promise<AddressResult | null> => {
  const res = await fetch(`${PHOTON_URL}/reverse?lon=${point[0]}&lat=${point[1]}&limit=1`);
  if (!res.ok) return null;
  const feature = (await res.json()).features?.[0];
  if (!feature) return null;
  const address = formatAddress(feature.properties);
  return { name: feature.properties.name || address.split(',')[0], address, longitude: point[0], latitude: point[1] };
};
